/**
 * Urban (U) vs extraurban (E) scope helpers.
 *
 * Future JSON may express scope as:
 *  - separate categories: linee_urbane / linee_extraurbane (and optionally fermate_extraurbane, …)
 *  - or an "Ambito" field on each dettaglio record: "urbano" | "extraurbano"
 *
 * See seta_piacenza_con_extraurbano_sondrio_v2.json for reference shape.
 */

const EXTRA_CATEGORY_BY_URBAN = {
  linee_urbane: "linee_extraurbane",
  fermate: "fermate_extraurbane",
  punti_vendita: "punti_vendita_extraurbane",
};

export function isRecordExtraurban(record) {
  if (!record || typeof record !== "object") return false;
  if (record.isExtraurban === true) return true;
  const ambito = String(record.ambito ?? record.Ambito ?? "").toLowerCase().trim();
  return ambito.includes("extra");
}

export function enrichRecordAmbito(record) {
  if (!record || typeof record !== "object" || Array.isArray(record)) return record;
  if (isRecordExtraurban(record)) {
    return { ...record, isExtraurban: true };
  }
  return record;
}

function getDettaglioFromCategory(cat) {
  if (!cat || typeof cat !== "object") return {};
  return (
    cat.dettaglio
    ?? cat["dettaglio fermate"]
    ?? cat["dettaglio punti vendita"]
    ?? cat["dettaglio linee"]
    ?? {}
  );
}

/** Merged dettaglio for a spatial category (urban + extraurban sources). */
export function getCategoryDettaglio(cityData, urbanKey) {
  if (!cityData) return {};

  const urbanCat = cityData[urbanKey];
  const extraKey = EXTRA_CATEGORY_BY_URBAN[urbanKey];
  const extraCat = extraKey ? cityData[extraKey] : null;

  const merged = {};
  for (const [id, rec] of Object.entries(getDettaglioFromCategory(urbanCat))) {
    merged[id] = enrichRecordAmbito(rec);
  }
  for (const [id, rec] of Object.entries(getDettaglioFromCategory(extraCat))) {
    merged[id] = enrichRecordAmbito({ ...rec, isExtraurban: true });
  }
  return merged;
}

/** Line lookup map for GTFS / schematic rendering (urban + extra linee). */
export function getLineDettaglio(cityData) {
  return getCategoryDettaglio(cityData, "linee_urbane");
}

export function hasExtraurbanCategory(cityData, urbanKey) {
  if (!cityData) return false;
  const extraKey = EXTRA_CATEGORY_BY_URBAN[urbanKey];
  if (extraKey && cityData[extraKey]) {
    const det = getDettaglioFromCategory(cityData[extraKey]);
    if (Object.keys(det).length > 0) return true;
    if (cityData[extraKey].punteggio_globale !== null && cityData[extraKey].punteggio_globale !== undefined) return true;
  }
  const merged = getCategoryDettaglio(cityData, urbanKey);
  return Object.values(merged).some(isRecordExtraurban);
}

export function hasUrbanCategory(cityData, urbanKey) {
  if (!cityData) return false;
  const urbanCat = cityData[urbanKey];
  if (urbanCat?.punteggio_globale !== null && urbanCat?.punteggio_globale !== undefined) return true;
  const det = getDettaglioFromCategory(urbanCat);
  if (Object.keys(det).length === 0) return !hasExtraurbanCategory(cityData, urbanKey);
  return Object.values(det).some((r) => !isRecordExtraurban(r))
    || Object.values(getCategoryDettaglio(cityData, urbanKey)).some((r) => !isRecordExtraurban(r));
}

/**
 * Which U/E legend rows apply for the current city.
 * When extraurban data is absent, only (U) rows are returned for fermate / punti vendita.
 */
export function getCityAmbitoAvailability(cityData) {
  const linee = {
    urban: hasUrbanCategory(cityData, "linee_urbane"),
    extraurban: hasExtraurbanCategory(cityData, "linee_urbane"),
  };
  const fermateDet = getCategoryDettaglio(cityData, "fermate");
  const pvDet = getCategoryDettaglio(cityData, "punti_vendita");
  const fermateKeys = Object.keys(fermateDet);
  const pvKeys = Object.keys(pvDet);

  const fermate = {
    urban: fermateKeys.some((k) => !isRecordExtraurban(fermateDet[k])) || (fermateKeys.length > 0 && !fermateKeys.some((k) => isRecordExtraurban(fermateDet[k]))),
    extraurban: fermateKeys.some((k) => isRecordExtraurban(fermateDet[k])),
  };
  const punti_vendita = {
    urban: pvKeys.some((k) => !isRecordExtraurban(pvDet[k])) || (pvKeys.length > 0 && !pvKeys.some((k) => isRecordExtraurban(pvDet[k]))),
    extraurban: pvKeys.some((k) => isRecordExtraurban(pvDet[k])),
  };

  if (!linee.urban && !linee.extraurban) linee.urban = true;
  if (fermateKeys.length === 0) { fermate.urban = true; fermate.extraurban = false; }
  if (pvKeys.length === 0) { punti_vendita.urban = true; punti_vendita.extraurban = false; }

  return { linee, fermate, punti_vendita };
}

export const MAP_LEGEND_DEFS = [
  { id: "linee_urban", group: "linee", urban: true, label: "Linee Urbane (U)" },
  { id: "linee_extra", group: "linee", urban: false, label: "Linee Extraurbane (E)" },
  { id: "fermate_urban", group: "fermate", urban: true, label: "Fermate (U)" },
  { id: "fermate_extra", group: "fermate", urban: false, label: "Fermate (E)" },
  { id: "pv_urban", group: "punti_vendita", urban: true, label: "Punti Vendita (U)" },
  { id: "pv_extra", group: "punti_vendita", urban: false, label: "Punti Vendita (E)" },
];

export function getMapLegendItems(cityData) {
  const avail = getCityAmbitoAvailability(cityData);
  return MAP_LEGEND_DEFS.filter((item) => {
    const scope = avail[item.group];
    return item.urban ? scope.urban : scope.extraurban;
  });
}

/** Sidebar servizi fisici — shows Linee Extraurbane when present; hides Linee Urbane when only extraurban data exists. */
export function getServiziFisiciForCity(baseMacros, cityData) {
  const urban = hasUrbanCategory(cityData, "linee_urbane");
  const extra = hasExtraurbanCategory(cityData, "linee_urbane");
  let macros = [...baseMacros];

  if (extra && !urban) {
    macros = macros.filter((m) => m.id !== "linee_urbane");
  }

  if (extra && !macros.some((m) => m.id === "linee_extraurbane")) {
    const extraDef = {
      id: "linee_extraurbane",
      // Extraurban-only operators (e.g. ATVO): show as "Linee" in the sidebar
      label: urban ? "Linee Extraurbane" : "Linee",
      icon: baseMacros.find((m) => m.id === "linee_urbane")?.icon,
      color: "#D9E9AD",
    };
    const idx = macros.findIndex((m) => m.id === "linee_urbane");
    macros.splice(idx >= 0 ? idx + 1 : 0, 0, extraDef);
  }

  return macros;
}

/** Line category key + score for reports (extraurban-only clients use linee_extraurbane). */
export function getLineeCategoryForCity(cityData) {
  const urban = hasUrbanCategory(cityData, "linee_urbane");
  const extra = hasExtraurbanCategory(cityData, "linee_urbane");
  if (extra && !urban) {
    return { key: "linee_extraurbane", label: "LINEE", barLabel: "Bus" };
  }
  if (urban || cityData?.linee_urbane) {
    return { key: "linee_urbane", label: "LINEE", barLabel: "Bus" };
  }
  return null;
}

export function getLineeScore(cityData) {
  const cat = getLineeCategoryForCity(cityData);
  if (!cat) return null;
  return cityData?.[cat.key]?.punteggio_globale ?? null;
}

export function getFermataMarkerColor(isExtraurban, isSelected) {
  if (isSelected) return "#8C74FF";
  return isExtraurban ? "#D9E9AD" : "#FFF260";
}

export function getPuntoVenditaMarkerColor(isExtraurban, isSelected) {
  if (isSelected) return "#8C74FF";
  return isExtraurban ? "#10b981" : "#FF852B";
}

/**
 * Place schematic route labels in a ring inside the city territory.
 * Coastal cities (e.g. Messina) get an inland offset so labels stay on land.
 */
export function getSchematicLinePositions(center, count, cityName = '') {
  const [lat0, lng0] = center;
  const key = String(cityName || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // [latOffset, lngOffset, radiusDegrees] — nudge ring inland / keep inside zone
  const CITY_RING = {
    MESSINA: [-0.012, -0.028, 0.022],          // west/inland of the Strait
    'SAN DONA DI PIAVE': [0.008, 0.0, 0.028],
    PALERMO: [0.01, 0.0, 0.025],
    COMO: [0.0, 0.0, 0.02],
    LECCE: [0.0, 0.0, 0.02],
    BARI: [0.008, -0.01, 0.022],
  };

  const ring = CITY_RING[key] || [0, 0, Math.max(0.018, 0.012 + count * 0.0015)];
  const [dLat, dLng, radius] = ring;
  const cLat = lat0 + dLat;
  const cLng = lng0 + dLng;
  const n = Math.max(count, 1);
  const positions = [];
  for (let i = 0; i < n; i += 1) {
    // Start from north and go clockwise for a stable layout
    const angle = -Math.PI / 2 + (i * Math.PI * 2) / n;
    positions.push([
      cLat + Math.cos(angle) * radius,
      cLng + Math.sin(angle) * radius,
    ]);
  }
  return positions;
}
